import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MyTooltip } from '@/webkit/MyTooltip';
import IconInfoCircle from '@/assets/icons/icon_info_circle_20.svg?react';
import { useGetDecisionWidget } from '@/query/queries/common/useGetDecisionWidget';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { buildVariables } from './formatVariables';
import { InvestorDecisionWidgetView } from './view';
import { useCollapseState } from './useCollapseState';
import { resolveCtaRoute, resolveCtaLabel } from './ctaConfig';
import { renderMessage, renderSecondary, renderTooltip } from './messageKeys';
import { DecisionWidgetOnboardingModal, ONBOARDING_STORAGE_KEY } from '@/modals/DecisionWidgetOnboardingModal';
import s from './index.module.scss';

export function InvestorDecisionWidget({ placement, amount }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const collapseState = useCollapseState(placement);
  const { modalIds, openSingleModal } = useModal();
  const isOnboardingOpen = !!modalIds[MODAL_IDS.decision_widget_onboarding];

  const { data: payload } = useGetDecisionWidget({
    placement,
    amount,
    enabled: !!placement,
  });

  const content = useMemo(() => {
    if (!payload || !payload.is_visible || !payload.message_key) return null;
    const variables = buildVariables(payload.variables, t);
    const rendered = renderMessage(payload.message_key, variables);
    if (!rendered) return null;
    return { ...rendered, variables };
  }, [payload, t]);

  useEffect(() => {
    if (!content) return;
    if (localStorage.getItem(ONBOARDING_STORAGE_KEY)) return;
    openSingleModal(MODAL_IDS.decision_widget_onboarding);
  }, [content, openSingleModal]);

  useEffect(() => {
    if (!isOnboardingOpen) return undefined;
    document.body.classList.add('decision-onboarding-active');
    return () => document.body.classList.remove('decision-onboarding-active');
  }, [isOnboardingOpen]);

  if (!content) return null;

  const secondaryNode = renderSecondary(payload.secondary_message_key, content.variables);

  const tooltipTemplate = renderTooltip(payload.tooltip_key, content.variables);
  const tooltipNode = tooltipTemplate && (
    <MyTooltip
      theme="dark"
      placement="top-end"
      elementClassName={s.tooltip_trigger}
      contentClassName={s.tooltip_above_chat}
      renderElement={() => (
        <div className={s.tooltip_trigger_inner}>
          <IconInfoCircle />
        </div>
      )}
      renderContent={() => tooltipTemplate}
    />
  );

  const ctaRoute = resolveCtaRoute(payload.cta_key);
  const ctaNode = ctaRoute && (
    <button type="button" className={s.cta_button} onClick={() => navigate(ctaRoute)}>
      {resolveCtaLabel(t, payload.cta_key)}
    </button>
  );

  return (
    <>
      {!isOnboardingOpen && (
        <InvestorDecisionWidgetView
          isCollapsed={collapseState.isCollapsed}
          isDismissed={collapseState.isDismissed}
          collapsedTitle={t('containers:decision_widget.collapsed_title')}
          titleNode={content.titleNode}
          messageNode={content.messageNode}
          secondaryNode={secondaryNode}
          tooltipNode={tooltipNode}
          ctaNode={ctaNode}
          onToggleCollapse={collapseState.toggleCollapse}
          onDismiss={collapseState.dismiss}
        />
      )}
      <DecisionWidgetOnboardingModal />
    </>
  );
}